<template>
    <div
        class="app"
        :class="{ 'app--full-height': $route.meta.isFullHeight }">
        <notifications :duration="5000" />

        <modal
            size="large"
            :open="isLegalModalOpen"
            @close="closeModal">
            <legal :is-primary="isPrimaryLegal"/>
        </modal>

        <modal
            size="large"
            :open="isPartnerLegalModalOpen"
            :heading="'Vivid Seats Offer Terms'"
            @close="closeModal">
            <p v-html="$config.partnerLegal"></p>
        </modal>
        <modal
            class="quebec-user-modal"
            size="large"
            :open="isQuebecUserModalOpen || isQuebecUserCheckoutModalOpen"
            @close="closeModal"
        >
            <h1 v-if="isQuebecUserModalOpen" class="quebec-user-modal__heading">Québec Resident?</h1>
            <h1 v-else class="quebec-user-modal__heading">We are unable to&nbsp;process&nbsp;your&nbsp;order.</h1>
            <div class="quebec-user-modal__content">Unfortunately, due to circumstances outside our control, we are not currently able to meet the needs of shoppers in Québec. If you are a resident of Québec, you will not be able to purchase tickets at this time. Thank you for your understanding!</div>
            <button class="quebec-user-modal__button" @click=closeModal>Got it</button>
        </modal>

        <modal
            size="large"
            v-if="bulletin && bulletin.legal"
            :open="isBulletinLegalOpen"
            @close="closeModal">
            <p v-html="bulletin.legal"></p>
        </modal>

        <modal
            class="app__login-modal"
            :open="isLoginModalOpen"
            @close="closeModal">
            <login-form
                class="app__login-form"
                username-placeholder="Account ID"
                :on-valid-submit="login"
                :username-initial-value="($config.auth || {}).loginUsernameDefault || ''">
                <div slot="intro">
                    <p class="app__login-intro">Please enter your Account ID and password below.</p>
                </div>
            </login-form>
        </modal>

        <modal
            size="large"
            :open="isWarningModalOpen"
            @close="closeModal">
            <warning />
        </modal>

        <pageHeader
            class="app__header"
            :bulletin="bulletin"
            :event="$store.state.event"
            :has-big-search="$route.meta.hasBigHeaderSearch"
            :has-logo-tab="hasLogoTab"
            :has-logout="Boolean($config.auth && $config.auth.hasLogout)"
            :has-my-account-button="Boolean($config.auth && $config.auth.isConcierge)"
            :has-my-tickets-button="Boolean($config.auth && $config.auth.hasMyTicketsButton)"
            :highlights="highlights"
            :highlightedSuggestion="highlightedSuggestion"
            :is-fluid="$route.meta.hasFluidHeader"
            :is-show-bulletin="$route.meta.hasBulletin"
            :is-show-menu="$route.meta.hasHeaderMenu"
            :is-show-page-not-found="isNotFoundPage"
            :is-show-partner-banner="$route.meta.hasPartnerBanner"
            :is-show-search="$route.meta.hasHeaderSearch"
            :is-tall="$route.meta.hasTallHeader"
            :logo-desktop="logoDesktop"
            :logo-mobile="logoMobile"
            :make-entity-route="makeEntityRoute"
            :on-decrement-highlighted-suggestion-index="decrementHighlightedSuggestionIndex"
            :on-increment-highlighted-suggestion-index="incrementHighlightedSuggestionIndex"
            :on-search="onSearch"
            :on-search-input-change="onSearchInputChange"
            :on-set-active-category="onSetActiveCategory"
            :on-suggestion-click="onSuggestionClick"
            :partner-logo-desktop="partnerLogoDesktop"
            :partner-logo-mobile="partnerLogoMobile"
            :query="query"
            :suggestions="suggestions"
            :userProfile="$store.state.userProfile"
            @clear-search-query="onClearSearchQuery"
        >
            <span
                v-if="$store.state.loyalty && $store.state.userProfile"
                class="app__welcome"
                slot="welcome">
                Welcome, {{ $store.state.userProfile.first_name }}
                <span class="welcome__user-info">
                    <span
                        v-if="creditCardType && lastFour"
                        class="user-info__credit-card">
                        <img
                            v-if="creditCardType"
                            class="user-info__credit-card__icon"
                            :alt="creditCardType.displayName"
                            :src="$store.state.imagesCdn + creditCardType.iconSlug">
                        <span class="user-info__middot" >&middot;&middot;&middot;&middot;</span>
                        <span class="user-info__last-four">{{ lastFour }}</span>
                        <span class="user-info__pipe">|</span>
                    </span>
                    <span class="user-info__loyalty-balance">{{ formatPoints($store.state.loyalty.balance) }} <span v-if="$store.state.loyalty.program_display_name">({{ $store.state.loyalty.program_display_name }})</span></span>
                </span>
            </span>
            <span
                v-if="$store.state.loyalty && $store.state.userProfile"
                class="app__welcome app__welcome--mobile"
                slot="welcome-mobile">
                <span class="welcome__user-info user-info-mobile" v-if="creditCardType && lastFour">
                    <span class="user-info__credit-card">
                        <img
                            v-if="creditCardType"
                            class="user-info__credit-card__icon"
                            :alt="creditCardType.displayName"
                            :src="$store.state.imagesCdn + creditCardType.iconSlug">
                        <span class="user-info__middot">&middot;&middot;&middot;&middot;</span>
                        <span class="user-info__last-four">{{ lastFour }}</span>
                    </span>
                    <span class="user-info__loyalty-balance">{{ formatPoints($store.state.loyalty.balance) }}</span>
                </span>
                <span class="icon--large icon-profile fx-hidden--large fx-hidden--full" v-else></span>
            </span>
        </pageHeader>
        <main
            class="app__main"
            :class="{ 'app__main--not-found': isNotFoundPage }">
            <router-view :key="$route.path" />
        </main>
        <footer v-if="hasFooter">
            <div class="footer__inner">
                <img
                    v-if="$config.hasCustomFooterLogo"
                    :alt="logoFooter.alt"
                    :src="logoFooter.url"
                    class="footer__custom-logo"
                >
                <img
                    alt="Vivid Seats logo"
                    class="footer__logo"
                    v-if="$config.hasPoweredByVividSeats"
                    :src="$store.state.imagesCdn + '/powered_by_VS_V2.svg'">
                <nav
                    class="footer__nav"
                    v-if="$route.meta.hasFooterNav">
                    <ul class="footer__nav-list">
                        <li class="footer__nav-item">
                            <router-link
                                class="footer__nav-link"
                                @click.native="onClickHelp"
                                :to="{ name: 'help' }">Help</router-link>
                        </li>
                        <li class="footer__nav-item">
                            <router-link
                                class="footer__nav-button"
                                @click.native="onClickLegal"
                                :to="{ query: updateQueryParams($route.query, { modal: 'legal' }) }">Legal</router-link>
                        </li>
                        <li class="footer__nav-item">
                            <router-link
                                class="footer__nav-button"
                                @click.native="onClickPrivacy"
                                :to="{ name: 'privacy' }">Privacy</router-link>
                        </li>
                    </ul>
                </nav>
            </div>
        </footer>
    </div>
</template>

<script>
import {
    findEntityByQuery,
    makeEntityRoute
} from './services/EntityService'
import {
    EVENT_NAME,
    FOOTER,
    HEADER
} from 'core/constants/googleAnalyticEvents'
import { CREDIT_CARD_TYPES } from 'core/types/CreditCard'
import { sliceBucketedSuggestions } from './services/SearchService'

import config from 'config'
import DateUtil from 'core/utilities/DateUtil'
import errorHandleable from './mixins/ErrorHandleable'
import legal from 'partnerLegal'
import loginForm from './components/LoginForm.vue'
import loyaltyFormattable from './mixins/LoyaltyFormattable'
import modal from 'core/components/ModalComponent.vue'
import pageHeader from './components/HeaderComponent.vue'
import warning from './components/Warning.vue'
import trackable from './mixins/Trackable'
import Utils from 'core/utilities/Utils.js'
import uuid from 'uuid'
import resizable from './mixins/Resizable'

export default {
    components: {
        legal,
        loginForm,
        modal,
        pageHeader,
        warning
    },
    computed: {
        allSuggestions () {
            if (!this.suggestions) { return [] }

            // We are mapping our bucketed suggestions into a flat Array
            // of { data, type } objects. Where:
            // data = suggestion
            // type = bucket key
            return Object.keys(this.suggestions).reduce((acc, groupKey) => {
                return acc.concat(this.suggestions[groupKey].map(suggestion => ({
                    data: suggestion,
                    type: groupKey
                })))
            }, [])
        },
        hasFooter () {
            return this.$route.meta.hasFooter && (this.$route.meta.hasFooterNav || this.$config.hasPoweredByVividSeats)
        },
        highlightedSuggestion () {
            return this.allSuggestions[this.highlightedSuggestionIndex] || null
        },
        isNotFoundPage () {
            return this.$route.name === 'not-found'
        }
    },
    created () {
        const highlights = this.$config.highlights
        const purchaseTicket = this.$store.state.purchaseTicket

        if (config.hasInsurance && !this.$store.state.sessionId) {
            this.$store.commit('setSessionId', uuid())
        }

        this.trackableEventCategory = 'UX - Homepage'

        this.$marketplace
            .getPromoAnnouncement()
            .then(response => {
                // TODO: Remove when API has been updated.
                if (typeof response.announcement === 'string') {
                    this.bulletin = Object.assign({}, this.$config.bulletin, {
                        message: response.announcement
                    })
                } else if (response.announcement) {
                    // TODO: We want to stop merging the config and the response once the API
                    // has been updated to return the legal copy.
                    this.bulletin = Object.assign({}, response.announcement, {
                        legal: (this.$config.bulletin || {}).legal || null
                    })
                } else {
                    this.bulletin = this.$config.bulletin
                }
            })
            .catch(err => {
                this.bulletin = this.$config.bulletin
                return Promise.reject(err)
            })
            .catch(err => this.catchAndLog(err))

        if (config.performerMenuLabels) {
            highlights.forEach(highlight => {
                highlight.name = config.performerMenuLabels[highlight.id] || highlight.name
            })
        }
        this.highlights = highlights

        if (config.excludedHighlights && config.excludedHighlights.length) {
            this.highlights = this.highlights.filter(function (item) {
                return !config.excludedHighlights.includes(item.id)
            })
        }

        if (!config.hasExclusiveHighlights) {
            this.highlights = this.highlights.filter(function (item) {
                return item.id !== 'exclusive'
            })
        }

        if ((config.logos || {}).desktop) {
            this.logoDesktop = {
                alt: config.logos.desktop.alt || '',
                overrideStyles: config.logos.desktop.overrideStyles || {},
                url: (this.$store.state.program || {}).logo_url || `${this.$store.state.imagesCdn}/${config.logos.desktop.staticImagePath}`
            }
        }

        if ((config.logos || {}).mobile) {
            this.logoMobile = {
                alt: config.logos.mobile.alt || '',
                overrideStyles: config.logos.mobile.overrideStyles || {},
                url: (this.$store.state.program || {}).logo_url || `${this.$store.state.imagesCdn}/${config.logos.mobile.staticImagePath}`
            }
        }

        if ((config.logos || {}).footer) {
            this.logoFooter = {
                alt: config.logos.footer.alt || '',
                url: (this.$store.state.program || {}).logo_url || `${this.$store.state.imagesCdn}/${config.logos.footer.staticImagePath}`
            }
        }

        this.hasLogoTab = Boolean((this.$store.state.program || {}).logo_url || config.hasLogoTab)

        if (((config.partnerSiteBanner || {}).logos || {}).desktop) {
            this.partnerLogoDesktop = {
                alt: config.partnerSiteBanner.logos.desktop.alt || '',
                overrideStyles: config.partnerSiteBanner.logos.desktop.overrideStyles || {},
                url: `${this.$store.state.imagesCdn}/${config.partnerSiteBanner.logos.desktop.staticImagePath}`
            }
        }

        if (((config.partnerSiteBanner || {}).logos || {}).mobile) {
            this.partnerLogoMobile = {
                alt: config.partnerSiteBanner.logos.mobile.alt || '',
                overrideStyles: config.partnerSiteBanner.logos.mobile.overrideStyles || {},
                url: this.partnerLogoDesktop.url // TODO: This assumes the existence of a Desktop version with a .url
            }
        }

        if (purchaseTicket && this.$store.state.userProfile) {
            if (config.hasMultiStepCheckout) {
                this.$router.replace({
                    name: 'checkout',
                    params: {
                        ticketId: purchaseTicket.ticketId
                    },
                    query: {
                        eventId: purchaseTicket.eventId,
                        step: Number(this.$route.query.step) || (this.$store.state.ticketOrder || {}).highestStep || 1
                    }
                })
            } else {
                this.$router.replace({
                    name: 'checkout',
                    params: {
                        ticketId: purchaseTicket.ticketId
                    },
                    query: {
                        eventId: purchaseTicket.eventId,
                        quantity: purchaseTicket.quantity
                    }
                })
            }
        }

        if (this.$store.state.loyalty && this.$store.state.loyalty.credit_card_account) {
            this.lastFour = this.$store.state.loyalty.credit_card_account.lastFour
            const userCreditCard = this.$store.state.loyalty.credit_card_account.network
            this.creditCardType = CREDIT_CARD_TYPES[userCreditCard.toUpperCase()]
        }
    },
    data () {
        return {
            bulletin: null,
            creditCardType: null,
            highlightedSuggestionIndex: -1,
            isBulletinLegalOpen: false,
            isLegalModalOpen: false,
            isLoginModalOpen: false,
            isPrimaryLegal: false,
            isQuebecUserModalOpen: false,
            isQuebecUserCheckoutModalOpen: false,
            isWarningModalOpen: false,
            lastFour: null,
            logoDesktop: null,
            logoFooter: null,
            logoMobile: null,
            partnerLogoDesktop: null,
            partnerLogoMobile: null,
            query: '',
            suggestions: null
        }
    },
    methods: {
        closeModal () {
            const query = Object.assign({}, this.$route.query)
            delete query.modal
            this.$router.replace({ query })
        },
        decrementHighlightedSuggestionIndex () {
            if (!this.suggestions) { return }

            this.highlightedSuggestionIndex -= 1
            if (this.highlightedSuggestionIndex < -1) {
                this.highlightedSuggestionIndex = this.allSuggestions.length - 1
            }
        },
        incrementHighlightedSuggestionIndex () {
            if (!this.suggestions) { return }

            this.highlightedSuggestionIndex += 1
            if (this.highlightedSuggestionIndex >= this.allSuggestions.length) {
                this.highlightedSuggestionIndex = -1
            }
        },
        login (username, password) {
            return this.$store
                .dispatch('loginLoyalty', {
                    username,
                    password
                })
                .then(() => {
                    if (this.$route.query.continue) {
                        this.$router.replace(this.$route.query.continue)
                    } else {
                        this.closeModal()
                    }
                })
                .catch(err => this.catchAndNotify(err))
        },
        makeEntityRoute,
        onClearSearchQuery () {
            this.query = ''
        },
        onClickHelp () {
            this.$googleTagManager.pushEvent(EVENT_NAME, FOOTER.HELP_CLICKED)
        },
        onClickLegal () {
            this.$googleTagManager.pushEvent(EVENT_NAME, FOOTER.LEGAL_CLICKED)
        },
        onClickPrivacy () {
            this.$googleTagManager.pushEvent(EVENT_NAME, FOOTER.PRIVACY_CLICKED)
        },
        onSetActiveCategory (activeCategory) {
            if (activeCategory) {
                this.trackClickEvent(`${activeCategory.name} Header Category - Clicked`)

                if (this.isWindowSmall) {
                    this.$googleTagManager.pushEvent(EVENT_NAME,
                        Object.assign({}, HEADER.MOBILE_NAVIGATION_TAXONOMY_SELECTED, {
                            value: activeCategory.id
                        }))
                }
            }
        },
        onSearch () {
            this.suggestions = null

            if (this.highlightedSuggestion) {
                this.$router.push(this.makeEntityRoute(this.highlightedSuggestion.type, this.highlightedSuggestion.data))
                this.onSuggestionClick(this.highlightedSuggestion)
            } else if (this.query) {
                let recentSearch = {
                    label: this.query,
                    route: {
                        name: 'search',
                        query: {
                            q: this.query
                        }
                    }
                }

                // TODO: deprecate this GA event in the future
                this.trackEnterEvent('Search Bar - Enter')
                this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.SEARCH_QUERY_SEARCHED)

                this.$store.state.userLocationPromise
                    .then(() => {
                        return this.$marketplace.search(this.query, {
                            all_in_price: this.$config.isAllInPricing ? 1 : 0,
                            per_page: 0,
                            page: 1,
                            utc_offset: DateUtil.format(new Date(), 'Z')
                        }, { timeout: 1500 })
                    })
                    .then(response => findEntityByQuery(this.query, (response.did_you_mean || {}).results || []))
                    .then(matchedEntity => {
                        if (!matchedEntity) { return }

                        recentSearch = {
                            label: matchedEntity.name,
                            route: this.makeEntityRoute(matchedEntity.type, matchedEntity)
                        }

                        return recentSearch
                    })
                    .catch(err => {
                        this.catchAndLog(err)
                    })
                    .finally(() => {
                        this.$store.commit('addRecentSearch', recentSearch)
                        this.$router.push(recentSearch.route)
                    })
            }
        },
        onSearchInputChange (query) {
            this.query = query
        },
        onSuggestionClick (suggestion) {
            // TODO: deprecate this GA event in the future
            this.trackClickEvent(`Suggested ${suggestion.type} Dropdown - Clicked`)
            this.$googleTagManager.pushEvent(EVENT_NAME,
                Object.assign({}, HEADER.SEARCH_SUGGESTION_SELECTED, {
                    value: suggestion.type
                }))

            this.query = ''
            this.suggestions = null

            this.$store.commit('addRecentSearch', {
                label: suggestion.data.name,
                route: makeEntityRoute(suggestion.type, suggestion.data)
            })
        },
        updateQueryParams: Utils.updateQueryParams
    },
    mixins: [
        errorHandleable,
        loyaltyFormattable,
        resizable,
        trackable
    ],
    watch: {
        $route: {
            immediate: true,
            handler () {
                this.isBulletinLegalOpen = this.$route.query.modal === 'bulletin-legal'
                this.isLegalModalOpen = this.$route.query.modal === 'legal'
                this.isPrimaryLegal = this.$route.query.legalType === 'primary'
                this.isLoginModalOpen = this.$route.query.modal === 'login'
                this.isPartnerLegalModalOpen = this.$route.query.modal === 'partner-legal'
                this.isWarningModalOpen = this.$route.query.modal === 'warning'
                this.isQuebecUserModalOpen = this.$route.query.modal === 'quebec-user'
                this.isQuebecUserCheckoutModalOpen = this.$route.query.modal === 'quebec-user-checkout'

                this.query = this.$route.query.q || ''
            }
        },
        query () {
            if (this.query.length < 2) {
                this.suggestions = null
            } else {
                this.$marketplace
                    .autocomplete(this.query, {
                        before: (request) => {
                            if (this.currrentAutocompleteRequest) {
                                this.currrentAutocompleteRequest.abort()
                            }
                            this.currrentAutocompleteRequest = request
                        }
                    })
                    .then(suggestions => {
                        this.currrentAutocompleteRequest = null
                        this.highlightedSuggestionIndex = -1
                        this.suggestions = sliceBucketedSuggestions(suggestions)
                    })
                    .catch(err => this.catchAndLog(err))
            }
        }
    }
}
</script>

<style lang="postcss">
@import "./styles/main.css";
</style>

<style lang="postcss" scoped>
@import "theme";

.warning-banner {
    background: #FCF1B6;
    color: #444649;
    padding: 8px;
    text-align: center;
    width: 100%;
    & a {
        color: #444649;
        text-decoration: underline;
    }
}

footer {
    border-top: 1px solid #e7e9e9;
    flex: 0 0 auto;
    padding: 32px 0;

    & .footer__inner {
        align-items: center;
        display: block;
        margin: 0 auto;
        max-width: 100%;
        padding: 0 var(--site-padding);
        width: var(--site-container-width);

        @media (--mq-large-min) {
            padding: 0 var(--site-padding) 80px 0;
            display: flex;
        }
    }

    & .footer__logo {
        height: 40px;
    }

    & .footer__nav-button,
    & .footer__nav-link {
        background: transparent;
        border: none;
        color: var(--c-gray-5);
        cursor: pointer;
        font-size: inherit;
        margin: 0;
        padding: 0;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    & .footer__nav {
        margin: 24px 0 0 auto;

        @media (--mq-large-min) {
            margin: 0 0 0 auto;
        }
    }

    & .footer__nav-list {
        display: flex;
        flex-direction: row;
        list-style: none;
    }

    & .footer__nav-item {
        margin-left: 20px;

        &:first-child {
            margin-left: 0px;
            @media (--mq-large-min) {
                margin-left: 20px;
            }
        }
    }

    & .footer__custom-logo {
        max-width: 75px;
        @media (--mq-large-min) {
            max-width: 143px;
        }
    }

}

.app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;

    &.app--full-height {
        height: 100vh;
    }

    & .app__header {
        flex: 0 0 auto;
    }

    & .app__login-form {
        padding: 0;
    }

    & .app__login-intro {
        margin: 0 0 30px;
        text-align: center;
    }

    & .app__login-modal {
        z-index: 50;
    }

    & .app__main {
        flex: 1 1 auto;
        position: relative;

        &.app__main--not-found {
            display: flex;
        }
    }

    & .app__welcome {
        display: inline-block;
        font-size: var(--fs-large);
        font-weight: var(--fw-normal);
        text-align: left;
        white-space: nowrap;

        &.app__welcome--mobile {
            text-align: left;
            & .icon-profile {
                position: absolute;
                right: 32px;
                top: 70px;
            }
        }

        & .user-info__mobile-top {
            display: flex;
        }
    }

    & >>> .account__welcome-icon {
        top: 10px;
    }

    & .app__welcome-points {
        font-size: var(--fs-small);
        font-weight: var(--fw-normal);
        display: flex;
    }

    & .user-info__credit-card__icon {
        height: auto;
        margin-right: 4px;
        padding-top: 0px;
        width: 22px;
    }

    & .welcome__user-info {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        &.user-info-mobile {
            flex-direction: column;
        }
        & .user-info__credit-card {
            align-items: center;
            display: flex;
            & .user-info__middot {
                font-weight: var(--fw-bold);
                margin-right: 4px;
            }

            & .user-info__pipe {
                font-weight: 200;
                margin: 0 2px;
                @media (--mq-large-min) {
                    margin: 0 4px;
                }
            }
        }

        & .user-info__last-four, & .user-info__loyalty-balance {
            font-size: var(--fs-small);
            font-weight: 200;
        }
    }

    @media (--mq-medium-min) {
        & .app__login-form {
            padding: 20px;
        }
    }
}

.quebec-user-modal {
    z-index: 50;

    & .quebec-user-modal__heading{
        color: #454F62;
        font-size: 20px;
        font-weight: 300;
        letter-spacing: -0.25px;
        line-height: 24px;
        margin-bottom: 0 0 18px 0;
        text-align: center;

        @media (--mq-large-min) {
            font-size: 32px;
            letter-spacing: -0.4px;
        }
    }
    & .quebec-user-modal__content{
        color: #353639;
        font-size: 14px;
        font-weight: 300;
        line-height: 16px;
        text-align: center;
        width: 100%;
    }
    & .quebec-user-modal__button{
        background-color: #1C74A5;
        background-color: var(--c-primary);
        border-radius: 4px;
        border: none;
        color: #FFFFFF;
        height: 38px;
        margin-top: 21px;
        width: 268px;

        @media (--mq-large-min) {
            margin-top: 36px;
        }
    }
    & >>>.close-button {
        display: none;
    }
    & >>>.modal {
        & .content {
            align-items: center;
            display: flex;
            flex-direction: column;

        }
    }

}
</style>
